import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Bell, Flame, TrendingUp, Award, Users, UserPlus } from "lucide-react";

export function SmartNotificationManager() {
  const { user } = useAuth();
  const { t } = useTranslation();

  // Streak & guideline & leaderboard notifications
  useEffect(() => {
    if (!user?.id) return;

    const checkInactivity = async () => {
      if (!user?.id) return;
      
      const { data: lastSession } = await supabase
        .from("flashcard_reviews")
        .select("reviewed_at")
        .eq("user_id", user.id)
        .order("reviewed_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!lastSession?.reviewed_at) return;

      const lastActivity = new Date(lastSession.reviewed_at);
      const daysSinceActivity = Math.floor(
        (Date.now() - lastActivity.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (daysSinceActivity === 2) {
        toast.info(t('common.notifications.streakDanger'), {
          description: t('common.notifications.streakDangerDesc'),
          icon: <Flame className="h-4 w-4 text-destructive" />,
          action: {
            label: t('common.notifications.review'),
            onClick: () => window.location.href = "/flashcards",
          },
        });
      }

      // Parallelize independent queries
      const [providerResult, challengesResult, colleagueQuizResult] = await Promise.all([
        supabase
          .from("healthcare_providers")
          .select("specialty")
          .eq("id", user.id)
          .maybeSingle(),
        (() => {
          const lastTop5NotificationKey = 'last_top5_notification';
          const lastTop5Notification = localStorage.getItem(lastTop5NotificationKey);
          const cooldownMs = 24 * 60 * 60 * 1000;
          const canShow = !lastTop5Notification || 
            (Date.now() - parseInt(lastTop5Notification, 10)) > cooldownMs;
          if (!canShow) return Promise.resolve(null);
          return supabase
            .from("challenge_participants")
            .select("challenge_id, progress_value")
            .eq("user_id", user.id)
            .gte("last_progress_update", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());
        })(),
        // Check how many colleagues completed today's quiz
        (() => {
          const lastColleagueNotif = localStorage.getItem('last_colleague_quiz_notification');
          const cooldownMs = 24 * 60 * 60 * 1000;
          const canShow = !lastColleagueNotif || 
            (Date.now() - parseInt(lastColleagueNotif, 10)) > cooldownMs;
          if (!canShow) return Promise.resolve(null);
          const today = new Date().toISOString().split('T')[0] as string;
          return supabase
            .from("daily_quiz_sessions")
            .select("user_id")
            .eq("quiz_date", today)
            .neq("user_id", user.id);
        })()
      ]);

      // Process guideline notifications
      const provider = providerResult?.data;
      if (provider?.specialty) {
        const { data: newGuidelines } = await supabase
          .from("guideline_notifications")
          .select("*")
          .eq("user_id", user.id)
          .eq("is_read", false)
          .gte("created_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

        if (newGuidelines && newGuidelines.length > 0) {
          toast.info(t('common.notifications.newGuideline'), {
            description: t('common.notifications.guidelineDesc', { count: newGuidelines.length, specialty: provider.specialty }),
            icon: <Bell className="h-4 w-4 text-primary" />,
            action: {
              label: t('common.notifications.see'),
              onClick: () => window.location.href = "/guidelines",
            },
          });
        }
      }

      // Process colleague quiz activity notification
      const colleagueQuizData = colleagueQuizResult && 'data' in colleagueQuizResult ? colleagueQuizResult.data : null;
      if (colleagueQuizData && colleagueQuizData.length >= 3) {
        toast.info(`${colleagueQuizData.length} collègues ont complété le quiz aujourd'hui ! 🧠`, {
          description: "Ne reste pas en arrière, tente le quiz du jour !",
          icon: <Users className="h-4 w-4 text-primary" />,
          action: {
            label: "Quiz du jour",
            onClick: () => window.location.href = "/quiz-du-jour",
          },
        });
        localStorage.setItem('last_colleague_quiz_notification', Date.now().toString());
      }

      // Process leaderboard
      const challenges = challengesResult && 'data' in challengesResult ? challengesResult.data : null;
      if (challenges && challenges.length > 0 && challenges[0]?.challenge_id) {
        const { data: leaderboard } = await supabase
          .from("challenge_leaderboard")
          .select("*")
          .eq("challenge_id", challenges[0].challenge_id)
          .order("progress_value", { ascending: false })
          .limit(10);

        if (!leaderboard) return;
        
        const userPosition = leaderboard.findIndex((entry) => entry.user_id === user.id);
        
        if (userPosition !== undefined && userPosition >= 0 && userPosition <= 5) {
          toast.success(t('common.notifications.top5'), {
            description: t('common.notifications.top5Desc', { position: userPosition + 1 }),
            icon: <TrendingUp className="h-4 w-4 text-success" />,
          });
          localStorage.setItem('last_top5_notification', Date.now().toString());
        }
      }
    };

    const initialTimeout = setTimeout(checkInactivity, 30 * 1000);
    const interval = setInterval(checkInactivity, 60 * 60 * 1000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [user]);

  // Real-time badge awards
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("badge_notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "user_badges",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const badgeName = (payload.new as any).badge_name;
          const badgeDesc = (payload.new as any).badge_description || "";
          if (badgeName) {
            toast.success(t('common.notifications.newBadge'), {
              description: `${badgeName}${badgeDesc ? `: ${badgeDesc}` : ""}`,
              icon: <Award className="h-4 w-4 text-accent" />,
              duration: 5000,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Real-time referral notifications
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("referral_notifications")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "referrals",
          filter: `referrer_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.new.status === 'completed' && payload.old.status !== 'completed') {
            toast.success(t('common.notifications.referralSuccess'), {
              description: t('common.notifications.referralDesc'),
              icon: <UserPlus className="h-4 w-4 text-primary" />,
              duration: 5000,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return null;
}
